import { useCallback, useEffect, useRef, useState } from 'react';
import { CHANGELOG, CHANGE_LABELS } from '../lib/changelog';

/** "2026-08-05T14:22:31+01:00" → "5 Aug 2026, 14:22". Empty string passes through. */
function formatStamp(iso: string): string {
  if (!iso) return 'unknown';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** git@gitlab.com:group/repo.git → group/repo */
function shortRemote(url: string): string {
  if (!url) return '—';
  return url
    .replace(/^git@[^:]+:/, '')
    .replace(/^https?:\/\/[^/]+\//, '')
    .replace(/\.git$/, '');
}

/**
 * The version badge, and the build/changelog panel behind it.
 *
 * The git fields come from a build-time snapshot rather than a runtime lookup —
 * the browser cannot shell out, and `connect-src 'none'` forbids fetching it.
 */
export function BuildInfo() {
  const ref = useRef<HTMLDialogElement>(null);
  const [open, setOpen] = useState(false);
  const git = __GIT_INFO__;

  const close = useCallback(() => ref.current?.close(), []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onClose = () => setOpen(false);
    el.addEventListener('close', onClose);
    return () => el.removeEventListener('close', onClose);
  }, []);

  return (
    <>
      <button
        type="button"
        className="version"
        onClick={() => setOpen(true)}
        title="Changelog and build info"
        aria-label={`Version ${__APP_VERSION__} — changelog and build info`}
      >
        v{__APP_VERSION__}
        {git.dirty && <span className="version-dirty" title="Built with uncommitted changes">*</span>}
      </button>

      <dialog
        ref={ref}
        className="explain-dialog"
        aria-labelledby="buildinfo-title"
        onClick={(e) => {
          if (e.target === ref.current) close();
        }}
      >
        <article className="explain">
          <header className="explain-head">
            <div>
              <p className="explain-kicker">Build</p>
              <h3 id="buildinfo-title">i-seng v{__APP_VERSION__}</h3>
            </div>
            <button
              type="button"
              className="ghost icon-btn explain-close"
              onClick={close}
              aria-label="Close"
            >
              ✕
            </button>
          </header>

          <section className="explain-section">
            <h4>This build</h4>
            <dl className="explain-facts">
              <div>
                <dt>commit</dt>
                <dd className="mono">
                  {git.commit || 'unknown'}
                  {git.dirty && <span className="dirty-tag">+ uncommitted changes</span>}
                </dd>
              </div>
              <div>
                <dt>branch</dt>
                <dd className="mono">{git.branch || 'unknown'}</dd>
              </div>
              <div>
                <dt>message</dt>
                <dd>{git.subject || '—'}</dd>
              </div>
              <div>
                <dt>committed</dt>
                <dd>{formatStamp(git.commitDate)}</dd>
              </div>
              <div>
                <dt>built</dt>
                <dd>{formatStamp(git.buildDate)}</dd>
              </div>
              <div>
                <dt>repository</dt>
                <dd className="mono">{shortRemote(git.remote)}</dd>
              </div>
            </dl>
            {git.dirty && (
              <div className="note" style={{ marginTop: 12 }}>
                <strong>This build is not reproducible.</strong> It was compiled with
                uncommitted changes in the working tree, so the commit above does not
                fully describe the code you are running.
              </div>
            )}
          </section>

          <section className="explain-section">
            <h4>Changelog</h4>
            <div className="release-list">
              {CHANGELOG.map((release) => (
                <div key={release.version} className="release">
                  <div className="release-head">
                    <span className="release-version mono">v{release.version}</span>
                    <span className="release-title">{release.title}</span>
                    <span className="release-date">{release.date}</span>
                  </div>
                  {release.model && (
                    <div className="release-model" title="Which model implemented this release">
                      built by {release.model}
                    </div>
                  )}
                  <ul className="release-changes">
                    {release.changes.map((c, i) => (
                      <li key={i}>
                        <span className={`change-kind kind-${c.kind}`}>
                          {CHANGE_LABELS[c.kind]}
                        </span>
                        {c.text}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        </article>
      </dialog>
    </>
  );
}
